import React, { useState, useEffect, useRef } from 'react';
import styles from './GameBoard.module.css';
import Heap from '../Heap/Heap';
import AudioController from '../AudioController/AudioController';
import { HeapNode, GameInstruction } from '../../types';
import { createParticleBurst } from '../../utils/particleEffects';
import soundManager from '../../utils/soundManager';

interface GameBoardProps {
  gameMode: 'min-heap' | 'max-heap';
  onGameModeChange?: () => void; // Callback for when game mode changes
}

const GameBoard: React.FC<GameBoardProps> = ({ gameMode, onGameModeChange }) => {
  // All nodes (including outlines) in a single array
  const [heapNodes, setHeapNodes] = useState<HeapNode[]>([]);
  const [entropyLevel, setEntropyLevel] = useState(0);
  const [instruction, setInstruction] = useState<GameInstruction | null>(null);
  // 'waiting' is the new state where the heap is valid and the user can choose to insert or remove
  const [gameState, setGameState] = useState<'waiting' | 'inserting' | 'removing' | 'bubbling'>('waiting');
  const [epoc, setEpoc] = useState(0);
  
  // Refs for initialization tracking
  const containerRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);

  // Effect to check heap validity when game mode changes
  useEffect(() => {
    // Skip this on initial render
    if (initializedRef.current) {
      console.log('Game mode changed, checking heap validity');
      
      // Set game state to bubbling if heap is invalid
      const realNodes = heapNodes.filter(n => !n.isOutline);
      const isValid = isHeapValid(realNodes);
      
      if (!isValid) {
        console.log('Heap is invalid after game mode change, entering bubbling state');
        setHeapNodes(currentNodes => {
          const realNodesOnly = currentNodes.filter(n => !n.isOutline);
          setGameState('bubbling');
          return markNodesNeedingSwapAndReturn(realNodesOnly, gameMode);
        });
      } else {
        // If valid, just update the heap state normally
        checkAndUpdateHeapState();
      }
      
      // Call the callback if provided
      if (onGameModeChange) {
        onGameModeChange();
      }
    }
  }, [gameMode, onGameModeChange]);

  // Initialize heap - only run once
  useEffect(() => {
    if (!initializedRef.current) {
      console.log('Initializing heap for the first time');
      // Start with a valid empty heap with an outline node at position 0
      checkAndUpdateHeapState();
      initializedRef.current = true;
    }
  }, [gameMode]);
  
  // Handle state inspection and instruction generation after state updates
  useEffect(() => {
    if (epoc > 0) {
      console.log('Epoc changed, inspecting heap state at:', epoc);
      // Check heap validity and update state accordingly
      checkAndUpdateHeapState();
    }
  }, [epoc]);

  // Function removed as this logic is now handled in generateNewInstruction

  // Generate next position for insertion
  const getNextInsertPosition = (currentNodes: HeapNode[]) => {
    // If tree is empty, only position is root (0)
    if (currentNodes.length === 0) {
      return 0;
    }
    
    // For a complete binary tree, the next position is always the number of nodes
    // This maintains the heap as an array where a new node is always added at the end
    return currentNodes.length;
  };
  
  // Check if a position already has a node in it
  const isPositionOccupied = (position: number, nodes: HeapNode[]) => {
    return nodes.some(node => node.position === position);
  };
  
  // Check if the heap is in a valid state
  const checkHeapValidityAndUpdateState = () => {
    // Filter to get only real nodes (non-outline)
    const realNodes = heapNodes.filter(node => !node.isOutline);
    
    // Check if the heap property is maintained
    const heapValid = isHeapValid(realNodes);
    console.log('Checking heap validity:', heapValid);
    
    if (heapValid) {
      // If heap is valid, show options for both insert and pop
      setUpValidHeapState();
    } else {
      // If heap is invalid, mark nodes that need swapping
      markNodesNeedingSwap(realNodes, gameMode);
      setGameState('bubbling');
    }
    
    return heapValid;
  };
  
  // Helper function for our initial checkpoint function - to be removed
  // We're keeping this function signature but redirecting to our new implementation
  const setUpValidHeapState = () => {
    console.log('setUpValidHeapState called - redirecting to checkAndUpdateHeapState');
    checkAndUpdateHeapState();
  };

  // Check and update the heap state
  const checkAndUpdateHeapState = () => {
    // Update the nodes based on heap validity
    setHeapNodes(currentNodes => {
      // First, separate real nodes from outline nodes
      const realNodes = currentNodes.filter(n => !n.isOutline);
      
      // Check if the heap is valid
      const heapValid = isHeapValid(realNodes);
      console.log('Checking heap validity:', heapValid, 'with', realNodes.length, 'nodes');
      
      if (!heapValid) {
        // If heap is invalid, mark nodes that need swapping and remove any outline nodes
        console.log('Heap is invalid, marking nodes that need swapping and removing outline nodes');
        setGameState('bubbling');
        return markNodesNeedingSwapAndReturn(realNodes, gameMode);
      }
      
      // For valid heaps, generate a new random value for insertion
      const value = Math.floor(Math.random() * 99) + 1;
      
      // Update instruction
      setInstruction({
        type: 'insert',
        value
      });
      
      // Clear animation flags but preserve justInserted flag
      const cleanNodes = realNodes.map(n => ({
        ...n,
        needsSwap: false,
        isSwapping: false,
        isBubbleUp: false,
        isBubbleDown: false
      }));
      
      // Create an outline node at the next position
      const nextPosition = getNextInsertPosition(cleanNodes);
      const outlineNode: HeapNode = {
        id: Date.now(),
        value,
        position: nextPosition,
        isOutline: true
      };
      
      console.log('Adding outline node at position', nextPosition, 'with value', value);
      
      // Set game state to "waiting" which means the heap is valid and user can choose to insert or pop
      setGameState('waiting');
      
      // Return the updated heap with the outline node
      return [...cleanNodes, outlineNode];
    });
    
    // Clear the justInserted flag after a brief delay
    setTimeout(() => {
      setHeapNodes(prevNodes => prevNodes.map(n => ({
        ...n,
        justInserted: false
      })));
    }, 200); // Very short timeout to allow responsiveness while preventing accidental removal
  };
  
  // Generate a new random instruction or check heap validity
  const generateNewInstruction = () => {
    console.log('Generating new instruction with current state:', { 
      nodeCount: heapNodes.length, 
      gameState, 
      epoc 
    });
    
    // Check and update heap state
    checkAndUpdateHeapState();
  };

  // Handle clicking on a real node
  const handleNodeClick = (node: HeapNode) => {
    // Don't allow removing a node that was just inserted
    if (node.justInserted) {
      console.log('Node was just inserted, ignoring removal click');
      return;
    }
    
    // If user clicks the root node and the heap is valid (in 'waiting' state), always pop
    if (node.position === 0 && gameState === 'waiting') {
      console.log('Root node clicked while heap is valid - popping root');
      createBurstEffectAtNode(node);
      removeRoot();
      return;
    }
    
    // If the heap is in removal state and root is clicked
    if (gameState === 'removing' && node.position === 0) {
      createBurstEffectAtNode(node);
      removeRoot();
      return;
    }
    
    // Handle swapping of nodes that need to be swapped to maintain heap property
    if (gameState === 'bubbling' || gameState === 'removing') {
      // Check if this node is marked as needing swap
      if (node.needsSwap) {
        // Find the other node that needs swapping (could be parent or child)
        const parentIndex = Math.floor((node.position - 1) / 2);
        const parentNode = heapNodes.find(n => n.position === parentIndex && n.needsSwap);
        
        if (parentNode) {
          // This node needs to swap with its parent
          swapNodeWithParent(node);
          return;
        }
        
        // Check if any child nodes need swapping with this node
        const leftChildIndex = 2 * node.position + 1;
        const rightChildIndex = 2 * node.position + 2;
        const leftChild = heapNodes.find(n => n.position === leftChildIndex && n.needsSwap);
        const rightChild = heapNodes.find(n => n.position === rightChildIndex && n.needsSwap);
        
        if (leftChild || rightChild) {
          // Determine which child to swap with based on heap type
          let childToSwap: HeapNode | undefined;
          
          if (leftChild && rightChild) {
            childToSwap = gameMode === 'min-heap'
              ? (leftChild.value < rightChild.value ? leftChild : rightChild)
              : (leftChild.value > rightChild.value ? leftChild : rightChild);
          } else {
            childToSwap = leftChild || rightChild;
          }
          
          if (childToSwap) {
            swapNodeWithParent(childToSwap);
            return;
          }
        }
      }
      
      // If we reach here, handle the case where nothing is marked but swapping would improve the heap
      const parentIndex = Math.floor((node.position - 1) / 2);
      if (parentIndex >= 0) {
        const parentNode = heapNodes.find(n => n.position === parentIndex);
        if (parentNode) {
          const needsSwap = gameMode === 'min-heap' 
            ? parentNode.value > node.value
            : parentNode.value < node.value;
          
          if (needsSwap) {
            swapNodeWithParent(node);
          }
        }
      }
    }
  };

  // Handle clicking on an outline node
  const handleOutlineClick = (node: HeapNode) => {
    console.log(`Outline node clicked:`, node);
    console.log(`Current game state: ${gameState}, isOutline: ${node.isOutline}`);
    
    // Allow clicking outline nodes in both 'inserting' and 'waiting' states
    // In 'waiting' state, the user can choose to either insert or remove (pop root)
    if ((gameState === 'inserting' || gameState === 'waiting') && node.isOutline) {
      console.log(`Processing valid outline node click for position ${node.position}`);
      
      // First, find the outline node in our heap nodes
      const outlineNode = heapNodes.find(n => n.id === node.id && n.isOutline);
      if (!outlineNode) {
        console.warn(`Outline node not found in heap nodes. Not proceeding.`);
        return;
      }
      
      // Insert the node at this position
      createBurstEffectAtNode(node);
      
      // Play sound effect
      soundManager.createBloopSound();
      
      // Mark the outline node as animating (swapping)
      setHeapNodes(prevNodes => prevNodes.map(n => {
        if (n.id === node.id) {
          return { ...n, isSwapping: true };
        }
        return n;
      }));
      
      // After animation, convert it to a real node by removing isOutline flag
      setTimeout(() => {
        // Convert outline to real node by removing the outline flag
        setHeapNodes(prevNodes => {
          const updatedNodes = prevNodes.map(n => {
            if (n.id === node.id) {
              return { 
                ...n, 
                isOutline: false, 
                isSwapping: false,
                // Add a just-inserted flag to prevent immediate removal
                justInserted: true
              };
            }
            return n;
          });
          
          console.log(`Converting outline to real node at position ${node.position}`);
          return updatedNodes;
        });
        
        // After conversion, check if the heap is valid
        setTimeout(() => {
          // Get the latest state
          const realNodes = heapNodes.filter(n => !n.isOutline);
          const heapValid = isHeapValid(realNodes);
          
          console.log('After insertion - Heap valid:', heapValid, 'nodes:', realNodes.length);
          
          if (heapValid) {
            console.log('Heap is valid after insertion, will add outline node');
            // Check and update the heap state - this will add a new outline node
            setTimeout(() => {
              checkAndUpdateHeapState();
            }, 50); // Extremely short delay to make it responsive
          } else {
            console.log('Heap is not valid after insertion, entering bubbling state');
            // Mark nodes that need swapping and remove outline nodes
            setHeapNodes(currentNodes => {
              // Filter out outline nodes and update game state
              const realNodesOnly = currentNodes.filter(n => !n.isOutline);
              setGameState('bubbling');
              return markNodesNeedingSwapAndReturn(realNodesOnly, gameMode);
            });
          }
        }, 100); // Very short delay to start validation sooner
      }, 100); // Very short delay to start animation sooner
    }
  };

  // Create particle effect at node position
  const createBurstEffectAtNode = (node: HeapNode) => {
    if (!containerRef.current) return;
    
    const nodeElement = containerRef.current.querySelector(`[data-position="${node.position}"]`);
    if (nodeElement) {
      const rect = nodeElement.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      const x = rect.left + rect.width / 2 - containerRect.left;
      const y = rect.top + rect.height / 2 - containerRect.top;
      
      // Create particle effect
      createParticleBurst(
        x, 
        y, 
        node.isOutline ? '#ffee94' : '#d4a5ff', 
        40, 
        containerRef.current
      );
    }
  };

  // Swap a node with its parent
  const swapNodeWithParent = (node: HeapNode) => {
    const parentIndex = Math.floor((node.position - 1) / 2);
    const parentNode = heapNodes.find(n => n.position === parentIndex);
    
    if (!parentNode) return;
    
    // Create particle effects at both nodes
    createBurstEffectAtNode(node);
    createBurstEffectAtNode(parentNode);
    
    // Play bubble sound
    soundManager.playBubbleUpSound();
    
    // First, add animation flags to the nodes and clear any needsSwap flags
    setHeapNodes(prevNodes => {
      const animatedNodes = prevNodes.map(n => {
        if (n.position === node.position) {
          return { ...n, isBubbleUp: true, needsSwap: false };
        }
        if (n.position === parentIndex) {
          return { ...n, isBubbleDown: true, needsSwap: false };
        }
        return n;
      });
      return animatedNodes;
    });
    
    // Then, after a short delay, perform the actual swap
    setTimeout(() => {
      // Use the function form of setState to ensure we have the latest state
      setHeapNodes(prevNodes => {
        // Create new heap with swapped nodes
        const newHeapNodes = prevNodes.map(n => {
          if (n.position === node.position) {
            return { 
              ...n, 
              position: parentIndex,
              isBubbleUp: false 
            };
          }
          if (n.position === parentIndex) {
            return { 
              ...n, 
              position: node.position,
              isBubbleDown: false 
            };
          }
          return n;
        });
        
        // Check if heap is now valid
        const realNodes = newHeapNodes.filter(n => !n.isOutline);
        const heapValid = isHeapValid(realNodes);
        console.log('Heap valid after swap:', heapValid, 'in state:', gameState);
        
        if (heapValid) {
          // If the heap is valid after swapping, we're done with the current operation
          console.log('Heap is now valid after swapping, scheduling check');
          // Very short timeout for faster response
          setTimeout(() => {
            // Trigger update to show the outline node for the next insertion
            checkAndUpdateHeapState();
          }, 50);
        } else {
          console.log('Heap still needs more swaps to be valid in state:', gameState);
          
          // Find and mark any nodes that still need swapping
          return markNodesNeedingSwapAndReturn(newHeapNodes, gameMode);
        }
        
        return newHeapNodes;
      });
    }, 100); // Very short delay to start animations sooner
  };

  // Remove the root node
  const removeRoot = () => {
    if (heapNodes.length === 0) return;
    
    // Find the root node
    const rootNode = heapNodes.find(n => n.position === 0);
    if (!rootNode) {
      console.error('Root node not found');
      return;
    }
    
    // Do not remove a node that was just inserted
    if (rootNode.justInserted) {
      console.log('Root node was just inserted, ignoring removal request');
      return;
    }
    
    // Play sound effect
    soundManager.playBubbleDownSound();
    
    // Create visual effect
    createBurstEffectAtNode(rootNode);
    
    // If it's the last node, just remove it
    if (heapNodes.length === 1) {
      // Add animation before removing
      const animatedNodes = heapNodes.map(n => ({
        ...n,
        isSwapping: true
      }));
      
      console.log('Removing last node with animation');
      setHeapNodes(animatedNodes);
      
      // Use a shorter timeout for faster response
      setTimeout(() => {
        setHeapNodes([]);
        // After removing the last node, always set up valid heap state
        setTimeout(() => {
          setUpValidHeapState();
        }, 100); // Very short delay for faster response
      }, 100); // Very short delay to start animations sooner
      return;
    }
    
    // Get the last node
    const lastNode = heapNodes.find(n => n.position === heapNodes.filter(n => !n.isOutline).length - 1);
    
    if (!rootNode || !lastNode) {
      console.error('Root node or last node not found during removal');
      return;
    }
    
    console.log('Animating removal - root:', rootNode.value, 'last:', lastNode.value);
    
    // First step: mark the nodes that will be animated
    const animatedNodes = heapNodes.map(n => {
      if (n.position === 0) {
        return { ...n, isSwapping: true };
      }
      if (n.id === lastNode.id) {
        return { ...n, isBubbleUp: true };
      }
      return n;
    });
    
    setHeapNodes(animatedNodes);
    
    // After first animation step, swap the root and last node
    setTimeout(() => {
      console.log('Swapping root and last node for removal');
      
      // Create a new array with the swapped positions
      const swappedNodes = animatedNodes.map(n => {
        if (n.position === 0) {
          // Move root to last position
          return { 
            ...n, 
            position: lastNode.position, 
            isSwapping: false 
          };
        }
        if (n.id === lastNode.id) {
          // Move last node to root
          return { 
            ...n, 
            position: 0, 
            isBubbleUp: false 
          };
        }
        return n;
      });
      
      setHeapNodes(swappedNodes);
      
      // After the swap animation completes, remove the old root (now at the end)
      setTimeout(() => {
        console.log('Removing old root node (now at the end)');
        
        // Use the function form of setState to ensure we're working with latest state
        setHeapNodes(currentNodes => {
          // Filter out the old root which is now at the last position
          const newHeapNodes = currentNodes.filter(n => n.id !== rootNode.id);
          
          // Check if heap is valid after removal
          const heapValid = isHeapValid(newHeapNodes);
          console.log('Heap valid after root removal:', heapValid);
          
          if (heapValid) {
            console.log('Heap is valid after removal, will add outline node');
            // Schedule an update to add the outline node
            setTimeout(() => {
              checkAndUpdateHeapState();
            }, 50); // Very short delay for faster response
            return newHeapNodes;
          } else {
            console.log('Heap is not valid after removal, user needs to fix it');
            // Update game state to bubbling to indicate the user needs to fix the heap
            setGameState('bubbling');
            
            // Remove any outline nodes and find nodes that need to be swapped
            const realNodesOnly = newHeapNodes.filter(n => !n.isOutline);
            
            // Find nodes that need to be swapped to maintain the heap property
            return markNodesNeedingSwapAndReturn(realNodesOnly, gameMode);
          }
        });
      }, 500); // Wait for the swap animation to complete
    }, 100); // Very short delay to start animations sooner
  };

  // Helper function that returns nodes with swap flags without directly updating state
  const markNodesNeedingSwapAndReturn = (nodes: HeapNode[], mode: 'min-heap' | 'max-heap'): HeapNode[] => {
    // First, filter out any outline nodes - we want to remove outlines when heap is invalid
    const realNodesOnly = nodes.filter(node => !node.isOutline);
    
    // Clear any existing needsSwap flags but preserve other flags
    const clearedNodes = realNodesOnly.map(node => ({
      ...node,
      needsSwap: false
      // Intentionally preserving other flags like justInserted
    }));
    
    // Find all parent-child pairs that need swapping
    const swapPairs: { parent: HeapNode, child: HeapNode }[] = [];
    
    // Check each node with its parent
    for (let i = 1; i < clearedNodes.length; i++) {
      const childNode = clearedNodes.find(n => n.position === i);
      if (!childNode) continue;
      
      const parentIndex = Math.floor((i - 1) / 2);
      const parentNode = clearedNodes.find(n => n.position === parentIndex);
      if (!parentNode) continue;
      
      const needsSwap = mode === 'min-heap'
        ? parentNode.value > childNode.value
        : parentNode.value < childNode.value;
      
      if (needsSwap) {
        swapPairs.push({ parent: parentNode, child: childNode });
      }
    }
    
    if (swapPairs.length > 0) {
      console.log(`Found ${swapPairs.length} pairs of nodes that need swapping`);
      
      // If there are multiple pairs, prioritize the one that will have the most impact
      // For min-heap: prioritize the pair with the smallest child value
      // For max-heap: prioritize the pair with the largest child value
      let pairToMark: { parent: HeapNode, child: HeapNode } | null = null;
      
      if (mode === 'min-heap') {
        // Find pair with smallest child for min-heap
        pairToMark = swapPairs.reduce((best, current) => {
          return !best || current.child.value < best.child.value ? current : best;
        }, null as { parent: HeapNode, child: HeapNode } | null);
      } else {
        // Find pair with largest child for max-heap
        pairToMark = swapPairs.reduce((best, current) => {
          return !best || current.child.value > best.child.value ? current : best;
        }, null as { parent: HeapNode, child: HeapNode } | null);
      }
      
      if (pairToMark) {
        // Mark the selected pair of nodes
        const markedNodes = clearedNodes.map(n => {
          if (n.id === pairToMark!.parent.id || n.id === pairToMark!.child.id) {
            return { ...n, needsSwap: true };
          }
          return n;
        });
        
        console.log(`Marking nodes for swap: parent=${pairToMark.parent.value}, child=${pairToMark.child.value}`);
        return markedNodes;
      }
    }
    
    // If no swaps needed, return the cleared nodes
    return clearedNodes;
  };
  
  // Helper function to mark nodes that need to be swapped (updates state directly)
  const markNodesNeedingSwap = (nodes: HeapNode[], mode: 'min-heap' | 'max-heap') => {
    // Get the updated nodes with swap markings
    const markedNodes = markNodesNeedingSwapAndReturn(nodes, mode);
    
    // Update the state with the marked nodes
    setHeapNodes(markedNodes);
  };

  // Check if the heap property is maintained
  const isHeapValid = (nodes: HeapNode[]) => {
    // Filter out any outline nodes - only check real nodes
    const realNodes = nodes.filter(n => !n.isOutline);
    
    for (let i = 0; i < realNodes.length; i++) {
      const leftChildIndex = 2 * i + 1;
      const rightChildIndex = 2 * i + 2;
      
      if (leftChildIndex < realNodes.length) {
        const parent = realNodes.find(n => n.position === i);
        const leftChild = realNodes.find(n => n.position === leftChildIndex);
        
        if (parent && leftChild) {
          if (gameMode === 'min-heap') {
            if (parent.value > leftChild.value) return false;
          } else {
            if (parent.value < leftChild.value) return false;
          }
        }
      }
      
      if (rightChildIndex < realNodes.length) {
        const parent = realNodes.find(n => n.position === i);
        const rightChild = realNodes.find(n => n.position === rightChildIndex);
        
        if (parent && rightChild) {
          if (gameMode === 'min-heap') {
            if (parent.value > rightChild.value) return false;
          } else {
            if (parent.value < rightChild.value) return false;
          }
        }
      }
    }
    
    return true;
  };

  // Get instruction text
  const getInstructionText = () => {
    // Get real nodes and check if heap is valid
    const realNodes = heapNodes.filter(node => !node.isOutline);
    const isHeapCurrentlyValid = isHeapValid(realNodes);
    
    console.log('Instruction text check - gameState:', gameState, 'isHeapValid:', isHeapCurrentlyValid);
    
    // If any nodes need swapping, that's the highest priority message
    const swapNodes = heapNodes.filter(n => n.needsSwap && !n.isOutline);
    if (swapNodes.length > 0) {
      return `Swap ${swapNodes[1]?.value} and ${swapNodes[0]?.value} to fix the heap.`;
    }
    
    // Otherwise, determine message based on game state
    switch (gameState) {
      case 'waiting':
        if (realNodes.length === 0) {
          return `Click the spiral to add the first node to your ${gameMode}`;
        }
        return `Click the spiral to add a new node, or click the top to pop it.`;
        
      case 'inserting':
        if (heapNodes.length === 0) {
          return `Click the spiral to add the first node to your ${gameMode}`;
        } else {
          return `Click the spiral to add a new node to your ${gameMode}`;
        }
        
      case 'bubbling':
        // This case should be handled by the swap nodes check above
        return `Click nodes to restore the ${gameMode} property`;
        
      case 'removing':
        if (heapNodes.length > 0 && heapNodes.some(n => n.position === 0 && !n.isOutline)) {
          const rootNode = heapNodes.find(n => n.position === 0 && !n.isOutline);
          return `Click the root (${rootNode?.value}) to remove it from the ${gameMode}`;
        }
        return `Click nodes to restore the ${gameMode} property`;
        
      default:
        return 'Build a valid binary heap by adding and removing nodes';
    }
  };

  return (
    <div className={styles.gameBoard} ref={containerRef}>
      <div className={styles.entropyMeter}>
        <div 
          className={styles.entropyFill} 
          style={{ width: `${entropyLevel}%` }}
        />
      </div>
      
      <div className={styles.instructionContainer}>
        <div className={styles.instruction}>
          {getInstructionText()}
        </div>
      </div>
      
      <div className={styles.heapParent}>
        {/* We no longer use separate outline nodes */}
        <Heap 
          nodes={heapNodes} 
          outlineNodes={[]}
          gameMode={gameMode}
          onNodeClick={handleNodeClick}
          onOutlineClick={handleOutlineClick}
        />
      </div>
      
      {/*<AudioController entropyLevel={entropyLevel} />*/}
    </div>
  );
};

export default GameBoard;